export const MAX_CERT_FILE_SIZE_BYTES = 5 * 1024 * 1024;
export const SAT_MAX_DATE_RANGE_DAYS = 31;
const SHA256_HEX_PATTERN = /^[a-f0-9]{64}$/i;
const RFC_PATTERN = /^[A-Z&Ñ]{3,4}[0-9]{6}[A-Z0-9]{3}$/;
const GENERIC_RFC_SET = new Set(['XAXX010101000', 'XEXX010101000']);

export const SAT_DOWNLOAD_TYPE_VALUES = ['emitidos', 'recibidos'];
const REQUEST_MODE_VALUES = [...SAT_DOWNLOAD_TYPE_VALUES, 'folio'];
export const SAT_REQUEST_TYPE_VALUES = ['CFDI', 'Metadata', 'PDF', 'TXTUUIDMASIVA'];
const SAT_REQUEST_TYPE_MAP = {
  cfdi: 'CFDI',
  metadata: 'Metadata',
  pdf: 'PDF',
  txtuuidmasiva: 'TXTUUIDMASIVA'
};
const CFDI_STATUS_VALUES = ['pendiente', 'aceptada', 'rechazada', 'verificando', 'completada', 'enviada', 'terminada'];
const CFDI_STATUS_ALIASES = {
  accepted: 'aceptada',
  aceptada: 'aceptada',
  recibida: 'aceptada',
  received: 'aceptada',
  en_proceso: 'verificando',
  'en proceso': 'verificando',
  processing: 'verificando',
  paquete_listo: 'completada',
  package_ready: 'completada',
  terminated: 'terminada',
  finished: 'terminada',
  completed: 'completada',
  ok: 'terminada',
  duplicate_skip: 'terminada',
  error: 'rechazada',
  rechazado: 'rechazada',
  reject: 'rechazada',
  rejected: 'rechazada'
};

function normalizeCfdiStatusValue(rawStatus, hasErrorCode) {
  const comparable = String(rawStatus || '').trim().toLowerCase();
  if (!comparable) return '';
  if (CFDI_STATUS_ALIASES[comparable]) {
    return CFDI_STATUS_ALIASES[comparable];
  }
  if (comparable.startsWith('error:')) {
    return 'rechazada';
  }
  if (comparable === 'ok') {
    return hasErrorCode ? 'rechazada' : 'terminada';
  }
  return comparable;
}

export function normalizeSatDownloadType(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return SAT_DOWNLOAD_TYPE_VALUES.includes(normalized) ? normalized : '';
}

function normalizeRequestMode(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return REQUEST_MODE_VALUES.includes(normalized) ? normalized : '';
}

export function normalizeSatRequestType(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return SAT_REQUEST_TYPE_MAP[normalized] || '';
}

export function buildSatRequestSemantics({
  mode,
  tipoSolicitud,
  downloadType,
  requestType,
  filters = {}
} = {}) {
  const warnings = [];
  const legacyRequestMode = normalizeRequestMode(mode);
  const legacyRequestType = normalizeSatRequestType(tipoSolicitud);
  const legacyDownloadType = normalizeSatDownloadType(downloadType);
  const canonicalRequestType = normalizeSatRequestType(requestType);
  const canonicalRequestMode = legacyRequestMode || legacyDownloadType;
  const canonicalDownloadType = canonicalRequestMode === 'folio'
    ? ''
    : legacyDownloadType || normalizeSatDownloadType(canonicalRequestMode);
  const normalizedRequestType = canonicalRequestType || legacyRequestType;

  if (
    legacyRequestMode &&
    canonicalDownloadType &&
    legacyRequestMode !== 'folio' &&
    legacyRequestMode !== canonicalDownloadType
  ) {
    warnings.push(
      `Conflicto SAT: mode="${legacyRequestMode}" no coincide con downloadType="${canonicalDownloadType}".`
    );
  }

  if (canonicalRequestType && legacyRequestType && canonicalRequestType !== legacyRequestType) {
    warnings.push(
      `Conflicto SAT: tipoSolicitud="${legacyRequestType}" no coincide con requestType="${canonicalRequestType}".`
    );
  }

  return {
    requestMode: canonicalRequestMode,
    downloadType: canonicalDownloadType,
    requestType: normalizedRequestType,
    canonicalRequestMode,
    canonicalDownloadType,
    canonicalRequestType: normalizedRequestType,
    legacyRequestMode,
    legacyRequestType,
    legacyDownloadType,
    legacyModo: legacyRequestMode,
    legacyTipoSolicitud: legacyRequestType,
    warnings,
    filters
  };
}

function buildDriveFolderUrl(folderId) {
  const value = String(folderId || '').trim();
  return value ? `https://drive.google.com/drive/folders/${value}` : '';
}

function buildDriveFileUrl(fileId) {
  const value = String(fileId || '').trim();
  return value ? `https://drive.google.com/file/d/${value}/view` : '';
}

function normalizeMirrorInfo(value) {
  const input = value && typeof value === 'object' ? value : {};
  return {
    origin: String(input.origin || '').trim(),
    mirrorOfSatPackage: Boolean(input.mirrorOfSatPackage),
    isAuthoritative: Boolean(input.isAuthoritative)
  };
}
const EFIRMA_AVAILABILITY_VALUES = ['disponible', 'vigente', 'por_vencer', 'vencida', 'incompleta', 'sin_carga'];

export const ERROR_MESSAGES = {
  AUTH_FAILED: 'Autenticacion rechazada por SAT. Verifica tus credenciales y passphraseRef.',
  REQUEST_REJECTED: 'SAT rechazo la solicitud. Ajusta filtros o tipo de solicitud e intenta de nuevo.',
  VERIFY_TIMEOUT: 'No fue posible confirmar la solicitud en tiempo. Reintenta la verificacion de estado.',
  PACKAGE_DOWNLOAD_FAILED: 'La solicitud se proceso, pero fallo la descarga de paquetes en backend.',
  SAT_BACKEND_NOT_READY: 'El backend SAT real no esta listo. Valida preflight y vuelve a intentar.',
  NO_SAT_PACKAGES: 'SAT no devolvio paquetes descargables para esta solicitud.',
  PREFLIGHT_UNAVAILABLE: 'No fue posible validar preflight SAT en este entorno. Verifica el endpoint DEV.'
};
const EMBEDDED_ERROR_CODE_PRIORITY = [
  'SAT_BACKEND_NOT_READY',
  'NO_SAT_PACKAGES',
  'PREFLIGHT_UNAVAILABLE',
  'INVALID_REQUEST'
];
export const EFIRMA_REUSE_MISSING_METADATA_MESSAGE = 'Material existe, pero falta metadata para reuso automatico.';

function normalizeComparableText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function mapEfirmaMessage(value) {
  const input = String(value || '').trim();
  if (!input) return '';
  const comparable = normalizeComparableText(input);
  if (comparable.includes('material existe pero falta metadata para reuso automatico')) {
    return EFIRMA_REUSE_MISSING_METADATA_MESSAGE;
  }
  return input;
}

function getDateRangeDiffDays(fechaInicial, fechaFinal) {
  const startDate = new Date(`${fechaInicial}T00:00:00.000Z`);
  const endDate = new Date(`${fechaFinal}T00:00:00.000Z`);
  const diffMs = endDate.getTime() - startDate.getTime();
  if (Number.isNaN(diffMs)) return Number.NaN;
  return Math.floor(diffMs / 86400000);
}

function getExtension(fileName = '') {
  const normalized = String(fileName).trim().toLowerCase();
  const index = normalized.lastIndexOf('.');
  return index === -1 ? '' : normalized.slice(index);
}

export function isValidSha256Hex(value) {
  return SHA256_HEX_PATTERN.test(String(value || '').trim());
}

export function validateCertificateFile(file, expectedExtension, maxSizeBytes = MAX_CERT_FILE_SIZE_BYTES) {
  if (!file) {
    return 'Selecciona un archivo antes de continuar.';
  }
  const extension = getExtension(file.name);
  if (extension !== expectedExtension) {
    return `Solo se permiten archivos ${expectedExtension}.`;
  }
  if (file.size > maxSizeBytes) {
    const maxMb = Math.round(maxSizeBytes / (1024 * 1024));
    return `El archivo supera el tamano maximo de ${maxMb} MB.`;
  }
  return '';
}

function toHex(buffer) {
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
}

export async function computeSha256Hex(file) {
  if (!file) return '';
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
  if (!cryptoApi?.subtle) {
    throw new Error('SHA-256 no disponible en este navegador');
  }
  const data = await file.arrayBuffer();
  const digest = await cryptoApi.subtle.digest('SHA-256', data);
  return toHex(digest);
}

export async function fileToBase64(file) {
  if (!file) return '';
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

export function validateCfdiRequestForm({
  clientRecordId,
  mode,
  tipoSolicitud,
  downloadType,
  requestType,
  passphraseRef,
  fechaInicial,
  fechaFinal,
  folio
}) {
  const semantics = buildSatRequestSemantics({ mode, tipoSolicitud, downloadType, requestType });
  const { requestMode, requestType: normalizedRequestType } = semantics;

  if (!clientRecordId) return 'Selecciona un cliente.';
  if (!requestMode) return 'Selecciona un modo valido.';
  if (!normalizedRequestType) return 'Selecciona un tipo de solicitud valido.';
  if (!String(passphraseRef || '').trim()) return 'passphraseRef es obligatorio.';

  if (requestMode === 'folio') {
    if (!String(folio || '').trim()) return 'El folio es obligatorio para este modo.';
    return '';
  }

  if (!fechaInicial || !fechaFinal) {
    return 'Debes indicar fecha inicial y fecha final.';
  }
  if (fechaInicial > fechaFinal) {
    return 'La fecha inicial no puede ser mayor a la fecha final.';
  }
  const diffDays = getDateRangeDiffDays(fechaInicial, fechaFinal);
  if (!Number.isNaN(diffDays) && diffDays > SAT_MAX_DATE_RANGE_DAYS) {
    return `El rango de fechas no puede exceder ${SAT_MAX_DATE_RANGE_DAYS} dias.`;
  }

  return '';
}

export function buildCfdiSecurePayload({
  client,
  mode,
  tipoSolicitud,
  downloadType,
  requestType,
  fechaInicial,
  fechaFinal,
  folio,
  passphraseRef,
  cerFile,
  keyFile,
  cerHash,
  keyHash,
  cerBase64,
  keyBase64,
  requestedBy,
  existingEfirma = null
}) {
  const empresa = String(client?.empresa || client?.name || '').trim();
  const idCliente = String(client?.idCliente || '').trim();
  const folderName = [empresa, idCliente].filter(Boolean).join('-');
  const semantics = buildSatRequestSemantics({ mode, tipoSolicitud, downloadType, requestType });
  const { requestMode, requestType: normalizedRequestType } = semantics;

  const payload = {
    request: {
      mode: requestMode,
      requestMode: semantics.canonicalRequestMode,
      downloadType: semantics.canonicalDownloadType,
      tipoSolicitud: normalizedRequestType,
      requestType: semantics.canonicalRequestType,
      legacyRequestMode: semantics.legacyRequestMode,
      legacyRequestType: semantics.legacyRequestType,
      legacyDownloadType: semantics.legacyDownloadType,
      filters:
        requestMode === 'folio'
          ? { folio: String(folio || '').trim() }
          : {
              fechaInicial,
              fechaFinal
            }
    },
    client: {
      recordId: client?.id || '',
      empresa,
      idCliente,
      rfc: String(client?.rfc || '').trim()
    },
    credentials: {
      passphraseRef: String(passphraseRef || '').trim(),
      reuseExisting: Boolean(existingEfirma?.isReusable),
      existingProfileId: existingEfirma?.profileId || '',
      certificates: [
        {
          type: 'cer',
          fileName: cerFile?.name || '',
          size: cerFile?.size || 0,
          sha256: cerHash || existingEfirma?.cerSha256 || '',
          contentBase64: cerBase64,
          mimeType: cerFile?.type || 'application/pkix-cert'
        },
        {
          type: 'key',
          fileName: keyFile?.name || '',
          size: keyFile?.size || 0,
          sha256: keyHash || existingEfirma?.keySha256 || '',
          contentBase64: keyBase64,
          mimeType: keyFile?.type || 'application/octet-stream'
        }
      ]
    },
    storage: {
      driveRoot: 'Clientes_Sensibles_ACN',
      clientFolder: folderName,
      forbidPublicClientesRoot: true
    },
    metadata: {
      requestedAt: new Date().toISOString(),
      requestedBy: requestedBy || 'dashboard',
      source: 'ci-frontend-cfdi-secure-v1'
    }
  };
  if (existingEfirma?.isReusable) {
    payload.credentials.certificates = payload.credentials.certificates.map(cert => ({
      ...cert,
      contentBase64: cert.contentBase64 || '',
      fileName: cert.fileName || '',
      size: cert.size || 0,
      existingAssetId:
        cert.type === 'cer'
          ? existingEfirma?.existingCerAssetId || ''
          : existingEfirma?.existingKeyAssetId || '',
      source: cert.contentBase64 ? 'upload' : 'existing'
    }));
  }
  const payloadError = validateCfdiSecurePayload(payload);
  if (payloadError) {
    throw new Error(payloadError);
  }
  return payload;
}

export function validateCfdiSecurePayload(payload = {}) {
  const semantics = buildSatRequestSemantics({
    mode: payload?.request?.mode,
    tipoSolicitud: payload?.request?.tipoSolicitud,
    downloadType: payload?.request?.downloadType,
    requestType: payload?.request?.requestType,
    filters: payload?.request?.filters || {}
  });
  const { requestMode, requestType } = semantics;
  const filters = payload?.request?.filters || {};
  const credentials = payload?.credentials || {};
  const certificates = Array.isArray(credentials.certificates) ? credentials.certificates : [];
  const cer = certificates.find(item => item?.type === 'cer');
  const key = certificates.find(item => item?.type === 'key');

  if (!requestMode) return 'Payload CFDI invalido: mode no permitido.';
  if (!requestType) return 'Payload CFDI invalido: tipoSolicitud no permitido.';
  if (!String(payload?.client?.recordId || '').trim()) return 'Payload CFDI invalido: client.recordId es obligatorio.';
  const clientRfc = String(payload?.client?.rfc || '').trim().toUpperCase();
  if (!clientRfc) return 'Payload CFDI invalido: client.rfc es obligatorio.';
  if (!RFC_PATTERN.test(clientRfc)) return 'Payload CFDI invalido: client.rfc no tiene formato RFC valido.';
  if (GENERIC_RFC_SET.has(clientRfc)) {
    return 'Payload CFDI invalido: client.rfc no puede ser RFC generico (XAXX010101000/XEXX010101000).';
  }
  if (!String(credentials.passphraseRef || '').trim()) return 'Payload CFDI invalido: passphraseRef es obligatorio.';
  if (String(credentials.passphrase || '').trim()) {
    return 'Payload CFDI invalido: no se permite passphrase en texto plano.';
  }
  if (String(payload?.passphrase || '').trim()) {
    return 'Payload CFDI invalido: no se permite passphrase en texto plano.';
  }
  if (!payload?.storage?.forbidPublicClientesRoot) {
    return 'Payload CFDI invalido: forbidPublicClientesRoot debe ser true.';
  }
  if (payload?.storage?.driveRoot !== 'Clientes_Sensibles_ACN') {
    return 'Payload CFDI invalido: driveRoot incorrecto.';
  }

  if (requestMode === 'folio') {
    if (!String(filters.folio || '').trim()) {
      return 'Payload CFDI invalido: folio es obligatorio para modo folio.';
    }
  } else {
    if (!filters.fechaInicial || !filters.fechaFinal) {
      return 'Payload CFDI invalido: fechaInicial y fechaFinal son obligatorias.';
    }
    if (filters.fechaInicial > filters.fechaFinal) {
      return 'Payload CFDI invalido: fechaInicial no puede ser mayor a fechaFinal.';
    }
    const diffDays = getDateRangeDiffDays(filters.fechaInicial, filters.fechaFinal);
    if (!Number.isNaN(diffDays) && diffDays > SAT_MAX_DATE_RANGE_DAYS) {
      return `Payload CFDI invalido: rango de fechaInicial/fechaFinal no puede exceder ${SAT_MAX_DATE_RANGE_DAYS} dias.`;
    }
  }

  const validateCertificate = (cert, expectedExtension, typeLabel) => {
    if (!cert) return `Payload CFDI invalido: certificado ${typeLabel} faltante.`;
    const usingExistingAsset = String(cert.existingAssetId || '').trim() && cert.source === 'existing';
    if (!usingExistingAsset && !String(cert.fileName || '').toLowerCase().endsWith(expectedExtension)) {
      return `Payload CFDI invalido: archivo ${typeLabel} debe terminar en ${expectedExtension}.`;
    }
    if (!usingExistingAsset && (!Number.isFinite(Number(cert.size)) || Number(cert.size) <= 0)) {
      return `Payload CFDI invalido: tamano de ${typeLabel} invalido.`;
    }
    if (!isValidSha256Hex(cert.sha256)) {
      return `Payload CFDI invalido: SHA-256 de ${typeLabel} invalido.`;
    }
    if (!usingExistingAsset && !String(cert.contentBase64 || '').trim()) {
      return `Payload CFDI invalido: contenido base64 de ${typeLabel} faltante.`;
    }
    if (usingExistingAsset && !String(cert.existingAssetId || '').trim()) {
      return `Payload CFDI invalido: referencia existente de ${typeLabel} faltante.`;
    }
    return '';
  };

  const cerError = validateCertificate(cer, '.cer', '.cer');
  if (cerError) return cerError;
  const keyError = validateCertificate(key, '.key', '.key');
  if (keyError) return keyError;

  return '';
}

export function buildEfirmaControlPayload({
  client,
  passphraseRef,
  cerFile,
  keyFile,
  cerHash,
  keyHash,
  cerBase64,
  keyBase64,
  requestedBy
}) {
  const empresa = String(client?.empresa || client?.name || '').trim();
  const idCliente = String(client?.idCliente || '').trim();
  const payload = {
    operation: 'upsert',
    client: {
      recordId: client?.id || '',
      empresa,
      idCliente,
      rfc: String(client?.rfc || '').trim()
    },
    credentials: {
      passphraseRef: String(passphraseRef || '').trim(),
      certificates: [
        {
          type: 'cer',
          fileName: cerFile?.name || '',
          size: cerFile?.size || 0,
          sha256: cerHash,
          contentBase64: cerBase64,
          mimeType: cerFile?.type || 'application/pkix-cert'
        },
        {
          type: 'key',
          fileName: keyFile?.name || '',
          size: keyFile?.size || 0,
          sha256: keyHash,
          contentBase64: keyBase64,
          mimeType: keyFile?.type || 'application/octet-stream'
        }
      ]
    },
    storage: {
      driveRoot: 'Clientes_Sensibles_ACN',
      clientFolder: [empresa, idCliente].filter(Boolean).join('-'),
      forbidPublicClientesRoot: true
    },
    metadata: {
      requestedAt: new Date().toISOString(),
      requestedBy: requestedBy || 'dashboard',
      source: 'ci-frontend-efirma-control-v1'
    }
  };
  const payloadError = validateCfdiSecurePayload({
    request: {
      mode: 'emitidos',
      tipoSolicitud: 'Metadata',
      filters: { fechaInicial: '2026-01-01', fechaFinal: '2026-01-01' }
    },
    ...payload
  });
  if (payloadError) {
    throw new Error(payloadError.replace('Payload CFDI invalido', 'Payload eFirma invalido'));
  }
  return payload;
}

export function normalizeEfirmaProfile(response = {}) {
  const fileEntries = Array.isArray(response?.files) ? response.files : [];
  const fileAssets = fileEntries.reduce((acc, item) => {
    const normalizedRole = String(item?.role || item?.type || '').trim().toLowerCase();
    if (!normalizedRole) return acc;
    if (normalizedRole === 'cer') {
      acc.cer = item;
    }
    if (normalizedRole === 'key') {
      acc.key = item;
    }
    return acc;
  }, {});
  const statusValue = String(response?.status || '').trim();
  const derivedAvailability =
    !response?.availability && !response?.vigencia && !response?.disponibilidad
      ? (/^ok(?::|$)/i.test(statusValue) ? 'disponible' : /^error(?::|$)/i.test(statusValue) ? 'incompleta' : statusValue)
      : '';
  const availability = String(
    response?.availability ||
    response?.vigencia ||
    derivedAvailability ||
    response?.status ||
    response?.disponibilidad ||
    'sin_carga'
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  const normalizedAvailability = EFIRMA_AVAILABILITY_VALUES.includes(availability) ? availability : 'sin_carga';
  const assets = response?.assets || fileAssets || {};
  const cerAsset = assets?.cer || {};
  const keyAsset = assets?.key || {};
  const cerSha256 = String(
    response?.cerSha256 || response?.cerHash || cerAsset?.sha256 || cerAsset?.hash || ''
  ).trim();
  const keySha256 = String(
    response?.keySha256 || response?.keyHash || keyAsset?.sha256 || keyAsset?.hash || ''
  ).trim();
  const hasStoredAssets = Boolean(
    String(response?.existingCerAssetId || cerAsset?.id || cerAsset?.fileId || assets?.cerId || '').trim() &&
    String(response?.existingKeyAssetId || keyAsset?.id || keyAsset?.fileId || assets?.keyId || '').trim()
  );
  const hasStoredHashes = isValidSha256Hex(cerSha256) && isValidSha256Hex(keySha256);
  const isAvailable = ['disponible', 'vigente', 'por_vencer'].includes(normalizedAvailability);
  const passphraseRef = String(
    response?.passphraseRef ||
    response?.passphrase_ref ||
    response?.credentials?.passphraseRef ||
    response?.credentials?.passphrase_ref ||
    response?.profile?.passphraseRef ||
    response?.profile?.passphrase_ref ||
    response?.fields?.['eFirma SAT Passphrase Ref'] ||
    response?.writebackPayload?.records?.[0]?.fields?.['eFirma SAT Passphrase Ref'] ||
    ''
  ).trim();
  const existingCerAssetId = String(
    response?.existingCerAssetId || cerAsset?.id || cerAsset?.fileId || assets?.cerId || ''
  ).trim();
  const existingKeyAssetId = String(
    response?.existingKeyAssetId || keyAsset?.id || keyAsset?.fileId || assets?.keyId || ''
  ).trim();
  const reuseChecklist = {
    hasCerHash: isValidSha256Hex(cerSha256),
    hasKeyHash: isValidSha256Hex(keySha256),
    hasCerAssetId: Boolean(existingCerAssetId),
    hasKeyAssetId: Boolean(existingKeyAssetId),
    hasPassphraseRef: Boolean(passphraseRef)
  };
  const reuseBlockers = [];
  if (!reuseChecklist.hasCerHash) reuseBlockers.push('CER hash faltante o invalido');
  if (!reuseChecklist.hasKeyHash) reuseBlockers.push('KEY hash faltante o invalido');
  if (!reuseChecklist.hasCerAssetId) reuseBlockers.push('cer asset id faltante');
  if (!reuseChecklist.hasKeyAssetId) reuseBlockers.push('key asset id faltante');
  if (!reuseChecklist.hasPassphraseRef) reuseBlockers.push('passphraseRef faltante');
  const reuseMetadataComplete = reuseBlockers.length === 0;
  const isReusable = isAvailable && hasStoredAssets && hasStoredHashes && reuseChecklist.hasPassphraseRef;
  const effectiveReuseBlockers = isReusable ? [] : reuseBlockers;
  const mappedMessage = mapEfirmaMessage(response?.message || response?.errorMessage || '');
  const reuseStatusReason = isReusable
    ? 'Reuso automatico habilitado.'
    : isAvailable && !reuseMetadataComplete
      ? `${EFIRMA_REUSE_MISSING_METADATA_MESSAGE} Pendiente: ${effectiveReuseBlockers.join(', ')}.`
      : isAvailable
        ? 'eFirma disponible, pero no reusable con la metadata actual.'
        : 'eFirma no disponible para reuso.';
  return {
    profileId: String(response?.profileId || response?.id || '').trim(),
    lastUploadAt: String(
      response?.lastUploadAt ||
      response?.uploadedAt ||
      response?.fechaUltimaCarga ||
      response?.writebackPayload?.records?.[0]?.fields?.['eFirma SAT Last Upload'] ||
      ''
    ).trim(),
    passphraseRef,
    availability: normalizedAvailability,
    isAvailable,
    isReusable,
    message: mappedMessage,
    reuseStatusReason,
    reuseBlockers: effectiveReuseBlockers,
    reuseChecklist,
    existingCerAssetId,
    existingKeyAssetId,
    cerFileName: String(response?.cerFileName || cerAsset?.fileName || cerAsset?.logicalFileName || '').trim(),
    keyFileName: String(response?.keyFileName || keyAsset?.fileName || keyAsset?.logicalFileName || '').trim(),
    cerSha256,
    keySha256,
    drivePath: String(response?.drivePath || response?.storagePath || '').trim()
  };
}

export function normalizeCfdiRunStatus(response = {}) {
  const idsPaquetesRaw =
    response?.idsPaquetes ??
    response?.ids_paquetes ??
    response?.packageIds ??
    response?.packages ??
    [];
  const idsPaquetes = Array.isArray(idsPaquetesRaw)
    ? idsPaquetesRaw.filter(Boolean).map(String)
    : typeof idsPaquetesRaw === 'string' && idsPaquetesRaw.trim()
      ? idsPaquetesRaw.split(',').map(item => item.trim()).filter(Boolean)
      : [];
  const packageHashesRaw = Array.isArray(response?.packageHashes)
    ? response.packageHashes
    : Array.isArray(response?.package_hashes)
      ? response.package_hashes
      : [];
  const packageHashes = packageHashesRaw
    .filter(item => item && typeof item === 'object')
    .map(item => ({
      idPaquete: String(item?.idPaquete || item?.id_paquete || item?.packageId || '').trim(),
      algorithm: String(item?.algorithm || item?.algoritmo || '').trim().toLowerCase(),
      hash: String(item?.hash || item?.digest || '').trim(),
      derived: Boolean(item?.derived)
    }))
    .filter(item => item.idPaquete || item.hash);
  const rawEstadoSolicitud = String(
    response?.estadoSolicitud ||
    response?.finalStatus ||
    response?.final_status ||
    response?.status ||
    ''
  ).trim().toLowerCase();
  const satErrorDetails = Array.isArray(response?.satError?.details)
    ? response.satError.details
        .map(item => String(item || '').trim())
        .filter(Boolean)
    : [];
  let rawErrorMessage = String(response?.errorMessage || response?.message || '').trim();
  if (!rawErrorMessage && satErrorDetails.length > 0) {
    rawErrorMessage = satErrorDetails.join(' | ');
  } else if (rawErrorMessage && satErrorDetails.length > 0) {
    const missingDetails = satErrorDetails.filter(detail => !rawErrorMessage.includes(detail));
    if (missingDetails.length > 0) {
      rawErrorMessage = `${rawErrorMessage} Detalle SAT: ${missingDetails.join(' | ')}`.trim();
    }
  }
  let errorCode = mapBackendErrorCode(response?.errorCode || response?.code || '');
  const embeddedErrorCode = inferEmbeddedErrorCode(rawErrorMessage);
  if (!errorCode || embeddedErrorCode) {
    errorCode = embeddedErrorCode || errorCode;
  }
  const estadoSolicitud = normalizeCfdiStatusValue(rawEstadoSolicitud, Boolean(errorCode));
  const metadataFolderId = String(response?.metadataFolderId || response?.metadata_folder_id || '').trim();
  const clientFolderId = String(response?.clientFolderId || response?.client_folder_id || '').trim();
  const evidenceFileId = String(response?.evidenceFileId || response?.evidence_file_id || '').trim();
  const evidenceFileName = String(response?.evidenceFileName || response?.evidence_file_name || '').trim();
  const requestType = normalizeSatRequestType(response?.requestType || response?.tipoSolicitud);
  const downloadType = normalizeSatDownloadType(response?.downloadType || response?.tipoDescarga || response?.modo);
  const satBackend = String(response?.satBackend || response?.sat_backend || '').trim().toLowerCase();
  const satDataOrigin = String(response?.satDataOrigin || response?.sat_data_origin || '').trim();
  const isAuthoritative = Boolean(response?.isAuthoritative);
  const sourceTrace = response?.sourceTrace && typeof response.sourceTrace === 'object' ? response.sourceTrace : null;
  const mirrorInfo = normalizeMirrorInfo(response?.mirrorInfo);
  const rawDownloadFiles = Array.isArray(response?.downloadFiles)
    ? response.downloadFiles
    : Array.isArray(response?.download_files)
      ? response.download_files
      : [];
  const downloadFiles = rawDownloadFiles
    .filter(item => item && typeof item === 'object')
    .map(item => ({
      idPaquete: String(item?.idPaquete || item?.id_paquete || item?.packageId || '').trim(),
      fileId: String(item?.fileId || item?.file_id || '').trim(),
      fileName: String(item?.fileName || item?.file_name || '').trim(),
      mimeType: String(item?.mimeType || item?.mime_type || '').trim(),
      driveUrl: String(item?.driveUrl || item?.drive_url || '').trim(),
      uploadError: item?.uploadError == null ? null : String(item.uploadError).trim(),
      mirrorInfo: normalizeMirrorInfo(item?.mirrorInfo)
    }))
    .filter(item => item.fileId || item.fileName || item.driveUrl);
  return {
    requestId: String(response?.requestId || response?.id || response?.executionRef || '').trim(),
    idSolicitudSat: String(
      response?.idSolicitudSat ||
      response?.idSolicitudSAT ||
      response?.idSolicitud ||
      response?.id_solicitud ||
      response?.reusedIdSolicitud ||
      response?.satRequestId ||
      ''
    ).trim(),
    requestType,
    downloadType,
    estadoSolicitud,
    idsPaquetes,
    packageHashes,
    satBackend,
    satDataOrigin,
    isAuthoritative,
    sourceTrace,
    mirrorInfo,
    errorCode,
    errorMessage: rawErrorMessage,
    rawEstadoSolicitud,
    rawErrorMessage,
    satErrorDetails,
    clientName: String(response?.clientName || response?.client_name || '').trim(),
    clientFolderId,
    clientFolderUrl: buildDriveFolderUrl(clientFolderId),
    metadataFolderId,
    metadataFolderUrl: buildDriveFolderUrl(metadataFolderId),
    evidenceFileId,
    evidenceFileName,
    evidenceFileUrl: buildDriveFileUrl(evidenceFileId),
    evidenceUploaded: Boolean(response?.evidenceUploaded || evidenceFileId),
    downloadFiles,
    runAt: String(response?.runAt || response?.run_at || '').trim(),
    finalizedAt: String(response?.finalizedAt || response?.finalized_at || '').trim()
  };
}

export function validateCfdiRunStatusShape(response = {}) {
  const normalized = normalizeCfdiRunStatus(response);
  if (!normalized.requestId) {
    return 'Respuesta CFDI invalida: requestId es obligatorio.';
  }
  if (!normalized.estadoSolicitud) {
    return 'Respuesta CFDI invalida: estadoSolicitud es obligatorio.';
  }
  if (
    normalized.estadoSolicitud &&
    !CFDI_STATUS_VALUES.includes(normalized.estadoSolicitud) &&
    !normalized.errorCode
  ) {
    return `Respuesta CFDI invalida: estadoSolicitud no reconocido (${normalized.estadoSolicitud}).`;
  }
  return '';
}

export function mapBackendErrorCode(input) {
  if (!input) return '';
  const raw = String(input).trim();
  if (!raw) return '';
  return raw.toUpperCase();
}

function inferEmbeddedErrorCode(input) {
  const upperText = String(input || '').toUpperCase();
  if (!upperText) return '';

  return EMBEDDED_ERROR_CODE_PRIORITY.find(code => upperText.includes(code)) || '';
}

export function resolveBackendErrorMessage(code, fallbackMessage = '') {
  const normalizedCode = mapBackendErrorCode(code);
  if (normalizedCode && ERROR_MESSAGES[normalizedCode]) {
    return ERROR_MESSAGES[normalizedCode];
  }
  return fallbackMessage || 'Ocurrio un error no controlado en el proceso CFDI.';
}

export function buildSubmitErrorMessage({
  errorCode,
  friendlyMessage,
  rawMessage,
  fallbackMessage
} = {}) {
  const normalizedCode = mapBackendErrorCode(errorCode);
  const fallback = fallbackMessage || 'Ocurrio un error no controlado en el proceso CFDI.';
  const detail = rawMessage || friendlyMessage || fallback;
  return normalizedCode ? `${normalizedCode}: ${detail}` : detail;
}
